import { useEffect, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { NotesTextEditorHandle, Tabs } from '@factorialco/f0-react/dist/experimental'
import { Ai } from '@factorialco/f0-react/icons/app'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { AiChat } from 'modules/meetings/components/AiChat'
import { MeetingNotes } from 'modules/meetings/components/MeetingNotes'
import { MeetingSkeleton } from 'modules/meetings/components/Skeletons/MeetingSkeleton'
import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { useMeetingTranscript } from 'modules/meetings/lib/hooks/useMeetingTranscript'
import { MeetingState } from 'modules/meetings/lib/hooks/useNotesTextEditorConfig'

import { PostMeetingTranscript } from './components/PostMeetingTranscript'

const MeetingPage = () => {
  const textEditorRef = useRef<NotesTextEditorHandle>(null)
  const [meetingState, setMeetingState] = useState<MeetingState | null>(null)
  const { loading: pageDetailsLoading, canUseAI, meeting, meetingId } = useMeeting()
  const [aiChatOpen, setAiChatOpen] = useState(canUseAI)
  const [showAiChat, setShowAiChat] = useState(canUseAI)
  const [activeTab, setActiveTab] = useState<'notes' | 'transcript'>('notes')
  const { transcript, loadingTranscript, refetchTranscript } = useMeetingTranscript()
  const { employee: currentEmployee } = useCurrentSession()
  const otherAttendee = meeting?.attendees?.filter(
    (attendee) => attendee.employee?.id !== currentEmployee?.id
  )?.[0]

  const haveTranscript = transcript && transcript.length > 0 ? true : false

  useEffect(() => {
    if (loadingTranscript || !transcript) return

    if (haveTranscript) {
      setMeetingState('post')
    } else {
      setMeetingState('pre')
    }
  }, [loadingTranscript, transcript, haveTranscript])

  useEffect(() => {
    setMeetingState(null)
    refetchTranscript()
    setActiveTab('notes')
  }, [refetchTranscript, meetingId])

  if (pageDetailsLoading || loadingTranscript || !meetingState) {
    return <MeetingSkeleton aiChatOpen={aiChatOpen} />
  }

  const toggleAiChat = () => {
    setAiChatOpen((prev) => {
      if (!prev) setTimeout(() => setShowAiChat(true), 500)
      else setShowAiChat(false)

      return !prev
    })
  }

  return (
    <Box height="full" flexDirection="column" style={{ overflow: 'hidden' }}>
      <Box flexDirection="row" flexGrow style={{ minHeight: '0', overflow: 'hidden' }}>
        <Box flexGrow style={{ minHeight: '0', overflow: 'hidden' }}>
          {haveTranscript && (
            <Box
              paddingTop="s8"
              flexDirection="row"
              justifyContent="spaceBetween"
              alignItems="center"
            >
              <Box width="full">
                <Tabs
                  activeTabId={activeTab}
                  tabs={[
                    {
                      id: 'notes',
                      label: i18n.t('meetings.meeting.notes.title'),
                      onClick: () => setActiveTab('notes'),
                    },
                    {
                      index: true,
                      id: 'transcript',
                      label: i18n.t('meetings.meeting.transcript.title'),
                      onClick: () => setActiveTab('transcript'),
                    },
                  ]}
                />
              </Box>
              {canUseAI && (
                <Box
                  paddingRight="s24"
                  flexNoShrink
                  border={{ bottom: { width: 's1', style: 'solid', color: 'grey200' } }}
                  height="full"
                  paddingTop="s4"
                >
                  <F0Button
                    label={i18n.t('meetings.meeting.chat.title')}
                    onClick={toggleAiChat}
                    icon={Ai}
                    variant="outline"
                  />
                </Box>
              )}
            </Box>
          )}
          <Box
            height="full"
            overflowY="hidden"
            width="full"
            minHeight="s0"
            flexDirection="row"
            justifyContent="center"
          >
            <Box
              height="full"
              overflowY="hidden"
              width="full"
              minHeight="s0"
              position="relative"
              flexDirection="column"
            >
              <Box style={{ display: activeTab === 'notes' ? 'flex' : 'none' }} height="full">
                <MeetingNotes
                  key={meetingId}
                  textEditorRef={textEditorRef}
                  meetingState={meetingState}
                  toggleAiChat={toggleAiChat}
                  haveTranscript={haveTranscript}
                />
              </Box>
              {activeTab === 'transcript' && <PostMeetingTranscript aiChatOpen={aiChatOpen} />}
            </Box>
          </Box>
        </Box>

        {canUseAI && (
          <Box
            height="full"
            overflowX="hidden"
            overflowY="hidden"
            style={{
              width: aiChatOpen ? '360px' : '0',
              transition: 'width 0.5s ease-in-out',
            }}
            border={{
              left: {
                width: 's1',
                style: 'solid',
                color: 'grey200',
              },
            }}
            flexNoShrink
          >
            {aiChatOpen && (
              <Box
                height="full"
                paddingBottom="s24"
                style={{
                  opacity: showAiChat ? '1' : '0',
                  transition: 'opacity 0.3s ease-in-out',
                }}
              >
                <AiChat
                  meetingId={Number(meetingId)}
                  textEditorRef={textEditorRef}
                  close={toggleAiChat}
                  otherAttendeeFirstname={otherAttendee?.employee?.firstName || ''}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default MeetingPage
